import { Popover, Tooltip } from 'antd';
import Button from 'components/common/Button';
import { SAVING_STATUS_SAVING_LABEL } from 'constants/uiConstants';
import React, { useState } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { LuLoader2 } from 'react-icons/lu';
import { MdError } from 'react-icons/md';
import MergeClassNames from 'utils/MergeClassNames';

const formatDuration = (seconds: number) => {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const secondsInMinute = 60;
  const secondsInHour = 60 * secondsInMinute;
  const secondsInDay = 24 * secondsInHour;
  const secondsInMonth = 30 * secondsInDay;

  const months = Math.floor(totalSeconds / secondsInMonth);
  const days = Math.floor((totalSeconds % secondsInMonth) / secondsInDay);
  const hours = Math.floor((totalSeconds % secondsInDay) / secondsInHour);
  const minutes = Math.floor((totalSeconds % secondsInHour) / secondsInMinute);
  const secs = totalSeconds % secondsInMinute;

  if (months > 0) {
    return days > 0 ? `${months}mo ${days}d` : `${months}mo`;
  }
  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${secs}s`;
};

const formatSavedAtTooltip = (value: number, name?: string, tz?: string) => {
  try {
    const formatter = new Intl.DateTimeFormat(undefined, {
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: tz,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(new Date(value));
    const month = parts.find((part) => part.type === 'month')?.value ?? '';
    const day = parts.find((part) => part.type === 'day')?.value ?? '';
    const hour = parts.find((part) => part.type === 'hour')?.value ?? '';
    const minute = parts.find((part) => part.type === 'minute')?.value ?? '';
    const dayPeriod =
      parts.find((part) => part.type === 'dayPeriod')?.value?.toLowerCase() ??
      '';
    const zone =
      parts.find((part) => part.type === 'timeZoneName')?.value ?? '';

    const timeValue = hour && minute ? `${hour}.${minute}${dayPeriod}` : '';
    const author = name?.trim() ? name.trim() : 'Unknown';

    return `${author}, ${month} ${day} at ${timeValue} ${zone}`.trim();
  } catch {
    const author = name?.trim() ? name.trim() : 'Unknown';
    const fallback = new Date(value).toLocaleString();
    return `${author}, ${fallback}`;
  }
};

type SavingStatus =
  | 'error'
  | 'retrying'
  | 'retry'
  | 'revert'
  | 'save'
  | 'saving'
  | 'saved';

const labelClass =
  'inline-flex items-center justify-center gap-1 rounded-[var(--radius-status-bar)] border px-3 py-1.5 text-sm font-semibold leading-none';

interface SavingStatusLabelProps {
  errorMessage?: string | null;
  showRetrying?: boolean;
  showRetry?: boolean;
  showRevert?: boolean;
  showSave?: boolean;
  /** A save is in flight with nothing waiting to be sent — for views that save straight away (e.g. Insights → Nutrition). */
  showSaving?: boolean;
  showSaved?: boolean;
  unsavedChanges?: number;
  timeSinceSave?: number;
  /** When the last save happened. Omit (with savedNote) to show Saved without a time. */
  savedAt?: number | null;
  savedBy?: string;
  timeZone?: string;
  /** Hover text for Saved instead of who saved and when. */
  savedNote?: string;
  onErrorClick?: () => void;
  onRetry?: () => void;
  onRevert?: () => void;
  onSave?: () => void;
  className?: string;
}

interface ErrorStatusProps {
  className?: string;
  errorDetail?: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  hasRevertAction?: boolean;
}

const ErrorStatus: React.FC<ErrorStatusProps> = ({
  className,
  errorDetail,
  isOpen,
  onOpenChange,
  hasRevertAction,
}) => (
  <Popover
    content={
      <div className="min-w-[13.75rem] max-w-xs">
        {errorDetail && <p className="text-sm text-gray-dark">{errorDetail}</p>}
        {hasRevertAction && (
          <p className="text-sm text-gray-dark">
            Or click the Revert button to discard the changes
          </p>
        )}
      </div>
    }
    trigger="click"
    placement="bottomRight"
    open={isOpen}
    onOpenChange={onOpenChange}
  >
    <button
      type="button"
      className={MergeClassNames(
        labelClass,
        'min-w-[6.75rem] border-gray-300 bg-white text-gray-dark hover:bg-gray-50',
        className
      )}
    >
      <div className="flex flex-col items-end gap-1">
        <span className="flex items-center gap-1 text-base font-semibold normal-case text-gray-dark">
          <span>Error</span>
          <MdError className="text-lg text-danger" />
        </span>
        <span className="text-[0.6875rem] font-medium normal-case text-gray-muted">
          Click to view details
        </span>
      </div>
    </button>
  </Popover>
);

interface RetryStatusProps {
  className?: string;
  onRetry?: () => void;
}

const RetryStatus: React.FC<RetryStatusProps> = ({ onRetry }) => (
  <Button
    variant="success"
    className="rounded-[var(--radius-status-bar)] px-2 py-1 text-white"
    size="sm"
    onClick={onRetry}
  >
    Retry
  </Button>
);

interface RetryingStatusProps {
  className?: string;
}

const RetryingStatus: React.FC<RetryingStatusProps> = ({ className }) => (
  <div
    className={MergeClassNames(
      labelClass,
      'border-information bg-information text-charcoal',
      className
    )}
  >
    Retrying...
  </div>
);

interface RevertStatusProps {
  className?: string;
  onRevert?: () => void;
}

const RevertStatus: React.FC<RevertStatusProps> = ({ onRevert }) => (
  <Button
    variant="secondary"
    className="rounded-[var(--radius-status-bar)] px-2 py-1 text-gray-dark hover:bg-secondary-light"
    size="sm"
    onClick={onRevert}
  >
    Revert
  </Button>
);

interface SaveStatusProps {
  className?: string;
  unsavedChanges: number;
  onSave?: () => void;
}

const SaveStatus: React.FC<SaveStatusProps> = ({
  className,
  unsavedChanges,
  onSave,
}) => (
  <div
    className={MergeClassNames(
      'flex min-w-[6rem] flex-col items-end rounded-[var(--radius-status-bar)] bg-gray-smoke px-1 py-1.5',
      className
    )}
  >
    <Button
      variant="success"
      size="sm"
      className="bg-success px-2 py-0.5"
      onClick={onSave}
    >
      Save
    </Button>
    {unsavedChanges > 0 && (
      <span className="mt-1 text-xs font-semibold normal-case text-gray-muted">
        {unsavedChanges} unsaved {unsavedChanges === 1 ? 'change' : 'changes'}
      </span>
    )}
  </div>
);

// The height of Saved with its "… ago" line, so a one-line label (Saving…,
// or Saved without a time) is the same size and the bar doesn't jump.
const STATUS_BOX_MIN_HEIGHT = 'min-h-[2.875rem] justify-center';

/** Same shape and icon size as Saved, so the label doesn't jump when a save completes. */
const SavingStatus: React.FC<{ className?: string }> = ({ className }) => (
  <div
    className={MergeClassNames(
      'flex min-w-20 flex-col items-end rounded-[var(--radius-status-bar)] bg-gray-smoke px-1 py-1.5',
      STATUS_BOX_MIN_HEIGHT,
      className
    )}
  >
    <div className="inline-flex items-center justify-center gap-1 text-sm font-semibold leading-none text-gray-muted">
      <span className="normal-case">{SAVING_STATUS_SAVING_LABEL}</span>
      <LuLoader2 className="animate-spin text-base" aria-hidden />
    </div>
  </div>
);

interface SavedStatusProps {
  className?: string;
  timeSinceSave: number;
  savedAt?: number | null;
  savedBy?: string;
  timeZone?: string;
  /** Hover text instead of who saved and when — for Saved without a time. */
  savedNote?: string;
}

const SavedStatus: React.FC<SavedStatusProps> = ({
  className,
  timeSinceSave,
  savedAt,
  savedBy,
  timeZone,
  savedNote,
}) => (
  <Tooltip
    title={
      savedNote ??
      (savedAt ? formatSavedAtTooltip(savedAt, savedBy, timeZone) : undefined)
    }
  >
    <div
      className={MergeClassNames(
        'flex min-w-20 flex-col items-end rounded-[var(--radius-status-bar)] bg-gray-smoke px-1 py-1.5',
        savedAt == null && STATUS_BOX_MIN_HEIGHT,
        className
      )}
    >
      <div className="inline-flex items-center justify-center gap-1 text-sm font-semibold leading-none text-gray-muted">
        <span className="normal-case">Saved</span>
        <FaCheckCircle className="text-base text-success" />
      </div>
      {savedAt != null && (
        <span className="mt-1 text-xs font-semibold normal-case text-gray-muted">
          {formatDuration(timeSinceSave)} ago
        </span>
      )}
    </div>
  </Tooltip>
);

const resolveStatus = (
  errorMessage?: string | null,
  showRetrying?: boolean,
  showRetry?: boolean,
  showRevert?: boolean,
  showSave?: boolean,
  showSaving?: boolean,
  showSaved?: boolean
): SavingStatus | null => {
  if (errorMessage) return 'error';
  if (showRetrying) return 'retrying';
  if (showRetry) return 'retry';
  if (showRevert) return 'revert';
  if (showSave) return 'save';
  if (showSaving) return 'saving';
  if (showSaved) return 'saved';
  return null;
};

const SavingStatusLabel: React.FC<SavingStatusLabelProps> = ({
  errorMessage,
  showRetrying,
  showRetry,
  showRevert,
  showSave,
  showSaving,
  showSaved,
  unsavedChanges = 0,
  timeSinceSave = 0,
  savedAt,
  savedBy,
  timeZone,
  savedNote,
  onErrorClick,
  onRetry,
  onRevert,
  onSave,
  className,
}) => {
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);

  const status = resolveStatus(
    errorMessage,
    showRetrying,
    showRetry,
    showRevert,
    showSave,
    showSaving,
    showSaved
  );

  const handleErrorPopoverChange = (open: boolean) => {
    if (open) {
      setIsErrorModalVisible(true);
      if (onErrorClick) {
        onErrorClick();
      }
    } else {
      setIsErrorModalVisible(false);
    }
  };

  const handleRevert = () => {
    if (onRevert) {
      onRevert();
    }
    setIsErrorModalVisible(false);
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
    setIsErrorModalVisible(false);
  };

  if (!status) return null;

  switch (status) {
    case 'error':
      return (
        <div className="flex flex-col-reverse items-center gap-2 md:flex-row">
          <div className="flex gap-2">
            {onRevert && <RevertStatus onRevert={handleRevert} />}
            {onRetry && <RetryStatus onRetry={handleRetry} />}
          </div>
          <ErrorStatus
            className={className}
            errorDetail={errorMessage ?? null}
            isOpen={isErrorModalVisible}
            onOpenChange={handleErrorPopoverChange}
            hasRevertAction={!!onRevert}
          />
        </div>
      );
    case 'retry':
      return <RetryStatus className={className} onRetry={onRetry} />;
    case 'retrying':
      return <RetryingStatus className={className} />;
    case 'revert':
      return <RevertStatus className={className} onRevert={onRevert} />;
    case 'save':
      return (
        <SaveStatus
          className={className}
          unsavedChanges={unsavedChanges}
          onSave={onSave}
        />
      );
    case 'saving':
      return <SavingStatus className={className} />;
    case 'saved':
      return (
        <SavedStatus
          className={className}
          timeSinceSave={timeSinceSave}
          savedAt={savedAt}
          savedBy={savedBy}
          timeZone={timeZone}
          savedNote={savedNote}
        />
      );
    default:
      return null;
  }
};

export default SavingStatusLabel;
